import { useAppDispatch, useAppSelector } from 'app/hooks';
import { updateProjectSetting } from 'features/project';
import { setExistingSystems } from 'features/structure';
import { useCallback } from 'react';
import {
  equipmentClearingSwitchDialog,
  isEquipmentClearingSystemPreference
} from 'utils/equipment-clearing-switch';
import { conflictModal } from 'utils/ui';

// Ducted/Hybrid → equipment-clearing pathway switch confirm (AHA-6681
// pathway exclusivity, widened for Hotel Style PTAC): wired as the
// system_preference selector's beforeChange. Arms only when switching INTO
// a pathway that never asks about ducted equipment (ductless, PTAC) FROM
// one that keeps it, while non-panel equipment is entered. Hybrid keeps
// equipment, so Ducted→Hybrid is silent. The reset-data-watcher's post-hoc
// wipe chain stays as-is (it also covers non-UI setters), but it has a hole
// (no wipe when project_type was already 'Designing New System') — this
// dialog sits IN FRONT at the selection UI and performs the removal
// explicitly on confirm, so the watcher wipe behind it is an idempotent
// no-op. Deselect-toggles and every equipment-keeping value pass through
// untouched.
export default function useEquipmentClearingSwitchGuard(): (
  nextValue?: string
) => boolean {
  const dispatch = useAppDispatch();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const currentPreferenceClearsEquipment = useAppSelector((state) =>
    isEquipmentClearingSystemPreference(
      state.project.projectSettings.find(
        (ps) => ps.code === 'system_preference'
      )?.value
    )
  );

  return useCallback(
    (nextValue?: string) => {
      // Panels live on level.electricalPanels, never in existingSystems, so
      // "any component" is exactly "any non-panel equipment".
      const hasNonPanelEquipment = existingSystems.some(
        (system) => system.components.length > 0
      );
      if (
        nextValue === undefined ||
        !isEquipmentClearingSystemPreference(nextValue) ||
        currentPreferenceClearsEquipment ||
        !hasNonPanelEquipment
      ) {
        return true;
      }
      conflictModal({
        ...equipmentClearingSwitchDialog(nextValue),
        confirmButtonText: 'Remove Equipment & Switch',
        cancelButtonText: 'Keep Current Selection',
        confirmAction: () => {
          // Panels survive the wipe on purpose — setExistingSystems([])
          // removes exactly the non-panel equipment (the app's established
          // ductless-wipe path, covered by the autosave watcher).
          dispatch(setExistingSystems([]));
          dispatch(
            updateProjectSetting({
              attributeCode: 'system_preference',
              value: nextValue
            })
          );
        },
        // The preference was never dispatched — nothing to undo.
        cancelAction: () => {}
      });
      return false;
    },
    [dispatch, existingSystems, currentPreferenceClearsEquipment]
  );
}
